import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { Database, ref, objectVal, update, set, push } from "@angular/fire/database";
import { of, switchMap } from "rxjs";
import { UtilService } from "./util.service";
import { TrackingService } from "./tracking.service";
import { AuthService } from "./auth.service";
import { ProfileService } from "./profile.service";
import { ClubService } from "./club.service";
import { ApiService } from "./api.service";
import { StorageService } from "./storage.service";
import { TranslateService } from "@ngx-translate/core";
import * as math from "mathjs/dist/math";

/**
 * League domain as a **signal store** on the modular Firebase SDK (Phase 2b).
 *
 * The current league is keyed on the `selectedLeagueId` signal — `init()` /
 * `select()` is just a signal write, and `switchMap` auto-tears-down the prior
 * league's stream on switch (replacing the old manual `init()` unsubscribe).
 * `latestLeague` / `leagueID` are kept as getters over the signals so the ~27
 * existing read sites need no changes (getter signal reads are tracked in
 * zoneless too). Cross-reads still hit the legacy Club/Profile services and
 * become fully reactive once those are migrated.
 */
@Injectable({ providedIn: "root" })
export class LeagueService {
  private readonly db = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  private readonly storageServ = inject(StorageService);

  readonly selectedLeagueId = signal<string | null>(null);
  private recalcOnLoad = false;

  private readonly leagueRaw = toSignal(
    toObservable(this.selectedLeagueId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.db, `leagues/${id}`))
            )
          : of(null)
      )
    ),
    { initialValue: null as any }
  );

  // Tournaments sorted by the club-cached timestamp. Cross-reads the still-
  // legacy ClubService; becomes fully reactive when Club is a signal store.
  private readonly sortedTournaments = computed(() => {
    const league = this.leagueRaw();
    if (!league?.tournaments) return undefined;
    const clubTourns = this.clubServ.latestClub?.tournaments || {};
    // The server writes the exact column list behind rankings[*][2..]
    // (rankingColumns) — a split night is ONE column there, named after the
    // parent, with childIDs. Prefer it; the membership-map derivation below
    // is the fallback for leagues not recalculated since that was added.
    if (Array.isArray(league.rankingColumns) && league.rankingColumns.length) {
      return league.rankingColumns.map((col: any) => {
        const clubTournament = clubTourns[col.tournID];
        return {
          ...col,
          tournName: clubTournament?.tournName ?? col.tournName,
          tournTimestamp:
            clubTournament?.tournTimestamp || col.tournTimestamp || 0,
          playerCount: clubTournament?.playerCount || 0,
          childIDs: col.childIDs || null,
        };
      });
    }
    // Fallback: one entry per membership key, folding split children (their
    // stub carries splitFrom) into a single entry keyed by the parent.
    const byNight: { [id: string]: any } = {};
    for (const [tournID, tournData] of Object.entries(league.tournaments) as [
      string,
      any,
    ][]) {
      const nightID = tournData?.splitFrom || tournID;
      const clubTournament = clubTourns[nightID];
      let entry = byNight[nightID];
      if (!entry) {
        entry = byNight[nightID] = {
          ...tournData,
          tournID: nightID,
          // Live club-cache name wins; the league snapshot's tournName is a
          // stale copy (only written when a league is first added to a
          // tournament, never on rename) and is kept only as a fallback for
          // tournaments no longer in the club cache (e.g. finalized leagues).
          tournName:
            clubTournament?.tournName ??
            (tournData?.splitFrom
              ? tournData.splitParentName ||
                String(tournData.tournName || "").replace(/\s\d+\/\d+$/, "")
              : tournData.tournName),
          tournTimestamp:
            clubTournament?.tournTimestamp ||
            clubTourns[tournID]?.tournTimestamp ||
            0,
          playerCount: clubTournament?.playerCount || 0,
          childIDs: tournData?.splitFrom ? [] : null,
        };
      }
      if (tournData?.splitFrom) {
        entry.childIDs.push(tournID);
        entry.playerCount += clubTourns[tournID]?.playerCount || 0;
      }
    }
    return Object.values(byNight).sort(
      (a: any, b: any) => a.tournTimestamp - b.tournTimestamp,
    );
  });

  /** Current league, merged with derived `sortedTournaments`. */
  readonly league = computed(() => {
    const l = this.leagueRaw();
    if (!l) return null;
    const sorted = this.sortedTournaments();
    return sorted ? { ...l, sortedTournaments: sorted } : l;
  });

  /** Legacy field — getter over the signal (kept so consumers don't change). */
  get latestLeague() {
    return this.league();
  }
  /** Legacy field — getter over the selected-id signal. */
  get leagueID(): string {
    return this.selectedLeagueId()!;
  }

  private _loading = signal(false);
  get loading() { return this._loading(); }
  set loading(v: boolean) { this._loading.set(v); }
  leagueWinnerPhotoBase64: string | null = null;

  constructor(
    private util: UtilService,
    private tracking: TrackingService,
    private auth: AuthService,
    private profServ: ProfileService,
    private clubServ: ClubService,
    private translate: TranslateService,
    private api: ApiService
  ) {
    math.import(
      {
        import: function () {
          throw new Error("Function import is disabled");
        },
        createUnit: function () {
          throw new Error("Function createUnit is disabled");
        },
        parse: function () {
          throw new Error("Function parse is disabled");
        },
        simplify: function () {
          throw new Error("Function simplify is disabled");
        },
        derivative: function () {
          throw new Error("Function derivative is disabled");
        },
      },
      { override: true }
    );

    // Recalc-on-load: when a league loads, kick a standings recalc if it carries
    // legacy `standings`, the recalc flag was set on select, or it has no
    // rankings yet. (Was the body of the old init() subscribe.)
    effect(() => {
      const league = this.leagueRaw();
      if (!league) return;
      if (league.standings || this.recalcOnLoad) {
        this.recalcOnLoad = false;
        this.recalculate();
      }
      if (!league.rankings) {
        this.api.recalculateLeagueStandings(this.selectedLeagueId()!);
      }
    });
  }

  private leagueRef() {
    return ref(this.db, `leagues/${this.selectedLeagueId()}`);
  }
  // Modular writes must run inside the injection context (AngularFire warns
  // otherwise — the same zone-stability rule as the read streams).
  private updateLeagueNode(data: any) {
    return runInInjectionContext(this.injector, () =>
      update(this.leagueRef(), data)
    );
  }

  /** Per-league website opt-out (Blind Valet Sites). Stored true/absent;
   *  the onLeagueHideChange trigger removes/restores the public standings. */
  setHideFromWebsite(hide: boolean) {
    return this.updateLeagueNode({ hideFromWebsite: hide || null });
  }

  /** Select the active league (legacy name `init`). Optional one-shot recalc. */
  init(leagueID: string, recalc?: boolean) {
    if (recalc) this.recalcOnLoad = true;
    this.selectedLeagueId.set(leagueID);
  }

  createLeague(leagueName: string, params: any) {
    const newRef = runInInjectionContext(this.injector, () =>
      push(ref(this.db, "leagues"))
    );
    const newKey = newRef.key;
    const create = Date.now();
    let leagueUpdate: any = {
      leagueName: leagueName,
      leagueID: newKey,
      clubID: this.clubServ.clubID,
      params: params,
      payouts: params.payouts,
      knockouts: params.knockouts,
      createDate: create,
      sort: -create,
    };
    runInInjectionContext(this.injector, () =>
      set(ref(this.db, `leagues/${newKey}`), leagueUpdate)
    ).then(() => {
      this.init(newKey);
      this.tracking.track("Create League", leagueUpdate);
      this.api.sendEvent("EVT_NEW_LEAGUE", this.profServ.latestProfile);
    });
    delete leagueUpdate.params;
    let clubUpdate: any = {};
    clubUpdate["leagues/" + newKey] = leagueUpdate;
    this.clubServ.clubAF.update(clubUpdate);
  }

  calcFinish(finish: number, params: any, cost?: number, kills?: number) {
    let scope: any = {
      p: params.playerCount,
      b: params.buyin,
      c: cost,
      f: finish,
      k: kills,
      z: params.prizepool,
      n: params.entries != null ? params.entries : params.playerCount,
      x: params.bullets != null ? params.bullets : 1,
      w: params.winnings != null ? params.winnings : 0,
      m: params.placesPaid != null ? params.placesPaid : 0,
      r: params.rebuys != null ? params.rebuys : 0,
      a: params.addons != null ? params.addons : 0,
      // Format code (1 standard / 2 split child / 3 shootout / 4 mystery) —
      // the dialog preview has no tournament context, so it previews as 1.
      t: params.formatCode != null ? params.formatCode : 1,
      // Shootout depth: rounds from the final (1 = final table); previews as 1.
      d: params.roundDepth != null ? params.roundDepth : 1,
    };
    let result: number;
    if (!params.formula) params.formula = this.league()?.params.formula;
    if (params.formula.indexOf("z") != -1 && !params.prizepool) scope.z = 0;
    try {
      result = math.round(math.eval(params.formula, scope), 2);
      return result;
    } catch (err) {
      // Expected while the user is mid-typing in the formula input — partial
      // expressions throw and we just render nulls until the formula is complete.
      return null;
    }
  }
  getTourneyPoints(params: any) {
    if (params.playerCount) {
      let points: number[] = [];
      for (var i = 0; i < params.playerCount; i++)
        points.push(this.calcFinish(i + 1, params, params.buyin, 0)!);
      return points;
    }
  }
  tournamentDateSort(a: any, b: any) {
    return a.timestamp - b.timestamp;
  }

  updateLeagueTournNames(leagueName: string) {
    this.api.updateLeagueTournNames(
      this.leagueID,
      leagueName,
      this.clubServ.clubID
    );
  }
  updateLeague(
    leagueName: string,
    scoringSystem: string,
    formula: string,
    payouts: boolean,
    knockouts: boolean,
    numberResults: number,
    countReentries: boolean
  ) {
    let leagueUpdate: any = { leagueName: leagueName };
    leagueUpdate["params/scoringSystem"] = scoringSystem;
    leagueUpdate["params/formula"] = formula;
    leagueUpdate["params/payouts"] = payouts;
    leagueUpdate["params/knockouts"] = knockouts;
    leagueUpdate["params/countReentries"] = countReentries;
    // Write null (deletes the RTDB key) when the field is cleared, so removing
    // the limit actually persists instead of leaving the old value in place.
    leagueUpdate["params/numberResults"] =
      numberResults > 0 ? numberResults : null;
    let clubUpdate: any = {};
    clubUpdate["leagues/" + this.leagueID + "/leagueName"] = leagueName;
    clubUpdate["leagues/" + this.leagueID + "/payouts"] = payouts;
    clubUpdate["leagues/" + this.leagueID + "/knockouts"] = knockouts;
    if (this.league()?.leagueName != leagueName)
      this.updateLeagueTournNames(leagueName);
    this.clubServ.clubAF.update(clubUpdate);
    this.updateLeagueNode(leagueUpdate).then(() => {
      setTimeout(() => {
        this.recalculate();
      });
      this.api.sendEvent(
        "EVT_LEAGUE_SETTINGS_UPDATED",
        this.profServ.latestProfile
      );
    });
  }

  isFinished(): boolean {
    return this.league()?.status === "finished";
  }
  async uploadLeagueWinnerPhoto(base64: string): Promise<string> {
    const url = await this.storageServ.uploadDataUrl(
      `${this.clubServ.clubID}/leagues/${this.leagueID}/winnerPhoto`,
      base64
    );

    await this.updateLeagueNode({ winnerPhotoURL: url });
    const clubUpdate: any = {};
    clubUpdate["leagues/" + this.leagueID + "/winnerPhotoURL"] = url;
    await this.clubServ.clubAF.update(clubUpdate);

    this.leagueWinnerPhotoBase64 = base64;
    return url;
  }
  async deleteLeagueWinnerPhoto(): Promise<void> {
    await this.storageServ.delete(
      `${this.clubServ.clubID}/leagues/${this.leagueID}/winnerPhoto`
    );

    await this.updateLeagueNode({ winnerPhotoURL: null });
    const clubUpdate: any = {};
    clubUpdate["leagues/" + this.leagueID + "/winnerPhotoURL"] = null;
    await this.clubServ.clubAF.update(clubUpdate);

    this.leagueWinnerPhotoBase64 = null;
  }
  finishLeague() {
    const rankings = this.league()?.rankings;
    if (!rankings || !rankings.length) return;
    const finishedDate = Date.now();
    const championUID = rankings[0][0];
    // Freeze a snapshot into separate fields so the live `rankings` (which the
    // recalc Cloud Function keeps overwriting) can never change a crowned
    // champion. `finalTournaments` preserves the {tournID, tournName} map so the
    // frozen podium still renders names if a tournament is later edited/removed.
    const leagueUpdate: any = {
      status: "finished",
      finishedDate: finishedDate,
      championUID: championUID,
      finalRankings: rankings,
      finalTournaments: this.league()?.tournaments || null,
    };
    // Dual-write the card-facing fields to the club-cache copy.
    const clubUpdate: any = {};
    clubUpdate["leagues/" + this.leagueID + "/status"] = "finished";
    clubUpdate["leagues/" + this.leagueID + "/finishedDate"] = finishedDate;
    this.clubServ.clubAF.update(clubUpdate);
    this.updateLeagueNode(leagueUpdate).then(() => {
      this.tracking.track("Finish League", { leagueID: this.leagueID });
    });
  }
  reopenLeague() {
    // Clear the frozen snapshot; live `rankings` is untouched so it is already
    // current. Keep `winnerPhotoURL` — the photo is independent of finished state.
    const leagueUpdate: any = {
      status: "active",
      finishedDate: null,
      championUID: null,
      finalRankings: null,
      finalTournaments: null,
    };
    const clubUpdate: any = {};
    clubUpdate["leagues/" + this.leagueID + "/status"] = "active";
    clubUpdate["leagues/" + this.leagueID + "/finishedDate"] = null;
    this.clubServ.clubAF.update(clubUpdate);
    this.updateLeagueNode(leagueUpdate).then(() => {
      this.tracking.track("Reopen League", { leagueID: this.leagueID });
    });
  }
  resetLeagueRankings() {
    this.updateLeagueNode({
      rankings: null,
    }).then(() => {
      this.recalculate();
    });
  }
  recalculate(leagueID?: string) {
    if (!leagueID) leagueID = this.leagueID;
    this.api.recalculateLeagueStandings(leagueID);
  }
  deleteLeague() {
    this.loading = true;
    this.api.deleteLeague(this.leagueID).then((res) => {
      this.loading = false;
    });
  }
  recalculateAllLeagues() {
    for (var l in this.clubServ.latestClub.leagues) this.recalculate(l);
    this.clubServ.saveMemberProcessing = false;
  }
}
